/* jm-offer.js — Personalized "Limited time offer for you" for JM-tagged SKUs.

   v3 — Safari-safe rewrite.

   What changed from v2:
   - Removed `contain: layout paint`. WebKit has had multiple bugs with
     `contain` applied to children of `position: sticky` ancestors;
     `.pdp__buybox` is sticky on desktop, which made this the next thing
     to suspect after we removed the transform animation.
   - Removed the CSS animation entirely. We tested in Chrome and the page
     stays alive with the animation on, but the user reports Safari still
     blanks. Any animation on a freshly-displayed child of a sticky parent
     is a known WebKit blank-tile risk, so we drop it.
   - DOM is built with `document.createElement` instead of `innerHTML`.
     This sidesteps the Safari HTML parser entirely when injecting SVG +
     buttons + text-with-em-dashes inside a sticky-positioned tree.
   - Everything else (try/catch wrappers, alive() check, throttled timer,
     sessionStorage cache, click-through to checkout) is unchanged.

   Behavior is identical: same 10s reveal, same 10-minute countdown,
   same Hoggle checkout flow, same `[jm-offer] …` console error prefix
   so anything that does throw shows up unambiguously. */
(function () {
  var REVEAL_DELAY_MS = 10 * 1000;
  var OFFER_TTL_MS    = 10 * 60 * 1000;
  var SS_PREFIX       = 'jm_offer_v';

  function warn(label, err) {
    try { console.error('[jm-offer] ' + label + ':', err); } catch (e) {}
  }
  function guard(label, fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (err) { warn(label, err); }
    };
  }

  /* ---- one-time CSS (no `contain`, no animation — see header) ---- */
  if (!document.getElementById('jm-offer-css')) {
    var st = document.createElement('style');
    st.id = 'jm-offer-css';
    st.textContent =
      '.jm-offer{display:none;margin:12px 0 4px;padding:14px 16px;border-radius:14px;' +
        'background:linear-gradient(135deg,#fff8ec 0%,#fef3f2 100%);' +
        'border:1px solid #f5d8a8;box-shadow:0 4px 16px rgba(180,120,20,.08);' +
        'font-family:"Inter",-apple-system,sans-serif;}' +
      '.jm-offer.is-visible{display:block;}' +
      '.jm-offer__eyebrow{display:flex;align-items:center;gap:7px;font-size:11px;font-weight:700;' +
        'letter-spacing:.12em;text-transform:uppercase;color:#9a6b1a;margin:0 0 8px;}' +
      '.jm-offer__spark{width:8px;height:8px;border-radius:50%;background:#e0a106;flex-shrink:0;' +
        'box-shadow:0 0 0 3px rgba(224,161,6,.18);}' +
      '.jm-offer__priceline{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:4px;}' +
      '.jm-offer__price{font-size:28px;font-weight:900;letter-spacing:-.03em;color:#0a0a0a;line-height:1;}' +
      '.jm-offer__was{font-size:14px;color:#9a8b6a;text-decoration:line-through;}' +
      '.jm-offer__save{font-size:11px;font-weight:800;color:#fff;background:#c0392b;border-radius:6px;' +
        'padding:2px 8px;letter-spacing:.02em;}' +
      '.jm-offer__timer{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600;' +
        'color:#8a6d3b;margin:6px 0 12px;}' +
      '.jm-offer__timer-ico{width:13px;height:13px;border:2px solid #b08a3a;border-radius:50%;' +
        'flex-shrink:0;display:inline-block;}' +
      '.jm-offer__timer strong{font-weight:800;color:#0a0a0a;font-variant-numeric:tabular-nums;}' +
      '.jm-offer__timer.is-urgent strong{color:#dc2626;}' +
      '.jm-offer__cta{display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;' +
        'padding:13px 18px;border:0;border-radius:12px;background:#0a0a0a;color:#fff;cursor:pointer;' +
        'font:800 14px "Inter",sans-serif;letter-spacing:-.005em;' +
        'transition:background .15s,opacity .15s;}' +
      '.jm-offer__cta:hover:not(:disabled){background:#1a1a1a;}' +
      '.jm-offer__cta:disabled{opacity:.45;cursor:not-allowed;background:#5a5a5a;}' +
      '.jm-offer__cta-arrow{display:inline-block;font-weight:700;}' +
      '.jm-offer__note{font-size:11px;color:#9a8b6a;text-align:center;margin:8px 0 0;line-height:1.4;}' +
      '.jm-offer.is-expired{background:#f6f6f6;border-color:#e5e5e5;box-shadow:none;}' +
      '.jm-offer.is-expired .jm-offer__eyebrow{color:#999;}' +
      '.jm-offer.is-expired .jm-offer__price{color:#9a9a9a;text-decoration:line-through;}';
    document.head.appendChild(st);
  }

  /* ---- helpers ---- */
  function fmt(cents, currency) {
    var n = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
    } catch (e) { return '$' + n.toFixed(2); }
  }

  function hoggleRoot() { return document.querySelector('[data-hoggle-root]'); }

  function parseJSONAttr(el, attr) {
    try { return JSON.parse(el.getAttribute(attr) || '{}'); } catch (e) { return {}; }
  }

  function floorForVariant(root, variantId) {
    if (!root) return 0;
    var salt = parseInt(root.getAttribute('data-rid'), 10) || 0;
    var vt = parseJSONAttr(root, 'data-vt');
    var key = String(variantId);
    if (vt[key] != null) {
      var dec = (parseInt(vt[key], 10) || 0) - salt;
      return dec > 0 ? dec : 0;
    }
    var tx = parseInt(root.getAttribute('data-tx'), 10) || 0;
    return tx > salt ? tx - salt : 0;
  }

  function priceForVariant(root, variantId) {
    if (!root) return 0;
    var prices = parseJSONAttr(root, 'data-variant-prices');
    var key = String(variantId);
    if (prices[key] != null) return parseInt(prices[key], 10) || 0;
    return parseInt(root.getAttribute('data-current-price-cents'), 10) || 0;
  }

  function generateOffer(floorCents, retailCents) {
    if (floorCents <= 0 || floorCents >= retailCents) return 0;
    var range = retailCents - floorCents;
    var t = (Math.random() < 0.10)
      ? 0.30 + Math.random() * 0.55
      : 0.68 + Math.random() * 0.20;

    var offer = floorCents + range * t;
    offer = Math.round(offer / 100) * 100;
    offer = offer - 1;

    if (offer >= retailCents) offer = retailCents - 1;
    if (offer < floorCents)   offer = floorCents;
    return offer;
  }

  function loadRecord(variantId) {
    try {
      var raw = sessionStorage.getItem(SS_PREFIX + variantId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function saveRecord(variantId, rec) {
    try { sessionStorage.setItem(SS_PREFIX + variantId, JSON.stringify(rec)); } catch (e) {}
  }

  /* ---- tiny DOM-builder helpers (replace innerHTML) ---- */
  function el(tag, opts) {
    var n = document.createElement(tag);
    if (opts) {
      if (opts.cls) n.className = opts.cls;
      if (opts.text != null) n.textContent = opts.text;
      if (opts.attrs) {
        for (var k in opts.attrs) {
          if (Object.prototype.hasOwnProperty.call(opts.attrs, k)) {
            n.setAttribute(k, opts.attrs[k]);
          }
        }
      }
    }
    return n;
  }
  function clearChildren(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  /* ---- the per-container controller ---- */
  function init(container) {
    if (container.dataset.jmBound === '1') return;
    container.dataset.jmBound = '1';

    var root = hoggleRoot();
    var currency = (root && root.getAttribute('data-currency')) || 'USD';
    var tagJM = container.getAttribute('data-product-tag-jm') === '1';
    var skuMap = parseJSONAttr(container, 'data-variant-skus');

    var tickHandle = null;

    function qualifies(variantId) {
      if (tagJM) return true;
      var sku = (skuMap[String(variantId)] || '').toUpperCase().trim();
      return /JM$/.test(sku);
    }

    function currentVariantId() {
      if (root && root.getAttribute('data-variant-id')) return root.getAttribute('data-variant-id');
      return container.getAttribute('data-current-variant-id');
    }

    function stop() { if (tickHandle) { clearTimeout(tickHandle); tickHandle = null; } }
    function alive() { return container && container.isConnected !== false && container.parentNode; }

    function hide() {
      stop();
      if (!alive()) return;
      container.classList.remove('is-visible', 'is-expired');
      clearChildren(container);
    }

    function renderExpired() {
      stop();
      if (!alive()) return;
      container.classList.add('is-visible', 'is-expired');
      clearChildren(container);

      var eyebrow = el('p', { cls: 'jm-offer__eyebrow' });
      var spark = el('span', { cls: 'jm-offer__spark' });
      spark.style.cssText = 'background:#bbb;box-shadow:none;';
      eyebrow.appendChild(spark);
      eyebrow.appendChild(document.createTextNode('Your private offer expired'));

      var note = el('p', {
        cls: 'jm-offer__note',
        text: 'This one-time price is no longer available. The list price still applies — or open Hoggle to negotiate.'
      });

      container.appendChild(eyebrow);
      container.appendChild(note);
    }

    function renderLive(variantId, offerCents, retailCents, expiresAt) {
      if (!alive()) return;
      var savings = retailCents - offerCents;
      var pct = retailCents > 0 ? Math.round((savings * 100) / retailCents) : 0;

      container.classList.remove('is-expired');
      container.classList.add('is-visible');
      clearChildren(container);

      // eyebrow
      var eyebrow = el('p', { cls: 'jm-offer__eyebrow' });
      eyebrow.appendChild(el('span', { cls: 'jm-offer__spark' }));
      eyebrow.appendChild(document.createTextNode('Limited time offer for you'));

      // priceline
      var pl = el('div', { cls: 'jm-offer__priceline' });
      pl.appendChild(el('span', { cls: 'jm-offer__price', attrs: { 'data-jm-price': '' }, text: fmt(offerCents, currency) }));
      pl.appendChild(el('span', { cls: 'jm-offer__was', text: fmt(retailCents, currency) }));
      if (savings > 0) {
        pl.appendChild(el('span', {
          cls: 'jm-offer__save',
          text: 'Save ' + fmt(savings, currency) + ' · ' + pct + '%'
        }));
      }

      // timer
      var timerWrap = el('div', { cls: 'jm-offer__timer', attrs: { 'data-jm-timer': '' } });
      timerWrap.appendChild(el('span', { cls: 'jm-offer__timer-ico', attrs: { 'aria-hidden': 'true' } }));
      var timerText = el('span');
      timerText.appendChild(document.createTextNode('Reserved for you — expires in '));
      var cd = el('strong', { attrs: { 'data-jm-countdown': '' }, text: '10:00' });
      timerText.appendChild(cd);
      timerWrap.appendChild(timerText);

      // CTA
      var cta = el('button', { cls: 'jm-offer__cta', attrs: { type: 'button', 'data-jm-cta': '' } });
      var ctaSpan = el('span', { text: 'Get this price — checkout' });
      cta.appendChild(ctaSpan);
      cta.appendChild(el('span', { cls: 'jm-offer__cta-arrow', text: '→', attrs: { 'aria-hidden': 'true' } }));

      // note
      var note = el('p', {
        cls: 'jm-offer__note',
        text: 'Discount auto-applies at checkout · one per customer · this exact price won’t come back.'
      });

      container.appendChild(eyebrow);
      container.appendChild(pl);
      container.appendChild(timerWrap);
      container.appendChild(cta);
      container.appendChild(note);

      function tick() {
        try {
          if (!alive()) { stop(); return; }
          var ms = expiresAt - Date.now();
          if (ms <= 0) { renderExpired(); return; }
          var m = Math.floor(ms / 60000);
          var s = Math.floor((ms % 60000) / 1000);
          if (cd) cd.textContent = m + ':' + (s < 10 ? '0' + s : s);
          if (ms < 60000 && timerWrap) timerWrap.classList.add('is-urgent');
          tickHandle = setTimeout(tick, 1000);
        } catch (err) { warn('tick', err); }
      }
      tick();

      cta.addEventListener('click', guard('cta-click', function () {
        if (Date.now() >= expiresAt) { renderExpired(); return; }
        cta.disabled = true;
        ctaSpan.textContent = 'Sending you to checkout…';
        goToCheckoutWithDeal(variantId, offerCents).catch(function (err) {
          warn('checkout', err);
          cta.disabled = false;
          ctaSpan.textContent = 'Hmm, try once more';
        });
      }));
    }

    function goToCheckoutWithDeal(variantId, agreedCents) {
      return fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: parseInt(String(variantId).replace(/.*\//, ''), 10),
                quantity: 1,
                properties: { _hoggle_agreed_cents: String(agreedCents) }
              }]
            })
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('cart/add failed');
          (window.top || window).location.href = '/checkout';
        });
    }

    function evaluate() {
      try {
        stop();
        if (!alive()) return;
        var variantId = currentVariantId();
        if (!variantId || !qualifies(variantId)) { hide(); return; }

        var retail = priceForVariant(root, variantId);
        var floor  = floorForVariant(root, variantId);
        if (floor <= 0 || floor >= retail) { hide(); return; }

        var rec = loadRecord(variantId);
        var now = Date.now();

        if (rec && rec.cents) {
          if (now >= rec.expiresAt) { renderExpired(); return; }
          renderLive(variantId, rec.cents, retail, rec.expiresAt);
          return;
        }

        if (rec && rec.revealAt && now < rec.revealAt) {
          container.classList.remove('is-visible');
          clearChildren(container);
          tickHandle = setTimeout(evaluate, rec.revealAt - now);
          return;
        }

        container.classList.remove('is-visible');
        clearChildren(container);
        var revealAt = now + REVEAL_DELAY_MS;
        saveRecord(variantId, { cents: 0, revealAt: revealAt, expiresAt: revealAt + OFFER_TTL_MS });

        tickHandle = setTimeout(guard('reveal', function () {
          if (!alive()) return;
          var offer = generateOffer(floor, retail);
          if (!offer) { hide(); return; }
          var expiresAt = Date.now() + OFFER_TTL_MS;
          saveRecord(variantId, { cents: offer, revealAt: Date.now(), expiresAt: expiresAt });
          renderLive(variantId, offer, retail, expiresAt);
        }), REVEAL_DELAY_MS);
      } catch (err) { warn('evaluate', err); }
    }

    document.querySelectorAll('select[name="id"], .pdp__variant-sel').forEach(function (sel) {
      sel.addEventListener('change', function () { setTimeout(evaluate, 30); });
    });
    document.querySelectorAll('.pdp__tvxl-pill[data-variant-id]').forEach(function (pill) {
      pill.addEventListener('click', function () { setTimeout(evaluate, 30); });
    });
    document.addEventListener('variant:change', function () { setTimeout(evaluate, 30); });

    evaluate();
  }

  function boot() {
    try {
      document.querySelectorAll('[data-jm-offer]').forEach(function (c) {
        try { init(c); } catch (err) { warn('init', err); }
      });
    } catch (err) { warn('boot', err); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();